import { useEffect, useRef, useState, KeyboardEvent } from 'react';

import { css, keyframes } from '@compiled/react';
import algoliasearchHelper from 'algoliasearch-helper';
import { useIntl, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { Button } from '@/components/wpl/button/src/button';
import { useFireAnalyticsEvents } from '@/hooks/use-fire-analytics-events';
import { wacToken } from '@/libraries/design/foundations';
import { Cookie } from '@/utils/cookie';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';
import { useFeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { AISummary } from './components/ai-summary';
import { Result } from './components/result';
import { SearchHelper } from './search-helper';

const ALGOLIA_SEARCH_INDEX_NAME_V3 = 'www.atlassian.com.v3';
const ALGOLIA_SEARCH_INDEX_NAME_V3_2 = 'www.atlassian.com.v3-2';
const ALGOLIA_DEV_SEARCH_INDEX_V3 = 'www.atlassian.com.v3_dev';
const ALGOLIA_FACET_KEY_CATEGORY = 'category';
const ALGOLIA_FACET_KEY_LOCALE = 'locale';

const TITLE_ONLY_SEARCH_WORD_COUNT_THRESHOLD = 3;

export type BXPSearchResult = {
	url: string;
	title: string;
	description: string;
	objectID: string;
	queryID: string;
	category: CategoryFilterName;
};

export const CATEGORY_FILTERS = [
	'All',
	'Articles',
	'Developer Resources',
	'Events',
	'Marketplace',
	'Purchasing FAQ',
] as const;

export type CategoryFilterName = (typeof CATEGORY_FILTERS)[number];

type FilterTranslations = {
	[K in CategoryFilterName]: string;
};

type FacetValues = {
	[K in CategoryFilterName]?: number;
};

export type SearchState = 'loading' | 'noResult' | 'hasResult' | 'hasMoreResult' | 'error' | null;

export const getObjects = (searchHelper: SearchHelper | undefined, objectIds: string[]) => {
	return searchHelper?.getObjects(objectIds);
};

export const renderSearchResultsMessage = (
	numResults: number,
	latestSearchText: string,
	currFilter?: CategoryFilterName,
) => {
	if (numResults === 0) {
		return (
			<FormattedMessage
				id="wac.search.pages.num.results.zero"
				defaultMessage="0 results for {latestSearchText}. Try again with a different term."
				values={{
					latestSearchText: <strong>{latestSearchText}</strong>,
					currentFilter: currFilter || 'All',
				}}
			/>
		);
	} else if (numResults === 1) {
		return (
			<FormattedMessage
				id="wac.search.pages.num.results.one"
				defaultMessage="{numResults} result for {latestSearchText} in {currentFilter}"
				values={{
					numResults,
					latestSearchText: <strong>{latestSearchText}</strong>,
					currentFilter: currFilter || 'All',
				}}
			/>
		);
	} else {
		return (
			<FormattedMessage
				id="wac.search.pages.num.results.many"
				defaultMessage="{numResults} results for {latestSearchText} in {currentFilter}"
				values={{
					numResults,
					latestSearchText: <strong>{latestSearchText}</strong>,
					currentFilter: currFilter || 'All',
				}}
			/>
		);
	}
};

// eslint-disable-next-line complexity
export const SearchTemplate = () => {
	const [numberOfHits, setTotalNumberOfHits] = useState<number>(0);
	const [hits, setHits] = useState<BXPSearchResult[]>([]);
	const [searchText, setSearchText] = useState<string>('');
	const [latestSearchText, setLatestSearchText] = useState('');
	const [searchState, setSearchState] = useState<SearchState>(null);
	const [currFilter, setCurrFilter] = useState<CategoryFilterName>('All');
	const [facetValues, setFacetValues] = useState<FacetValues>({});
	const searchHelper = useRef<SearchHelper>();
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [queryId, setQueryId] = useState<string>('');
	const [isActive, setIsActive] = useState<boolean>(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const handleFocus = () => setIsActive(true);
	const handleBlur = () => setIsActive(false);
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(e.target.value);
	};
	const handleClear = () => {
		setSearchText('');
		setIsActive(false);
	};

	const wacAISearchSummaryExperiment = useExperiment(experiments.wacAISearchSummaryV2);
	const isAISearchSummaryEnabled =
		wacAISearchSummaryExperiment.type === 'enrolled' && wacAISearchSummaryExperiment.value;

	const searchCtaExperiment = useExperiment(experiments.wacSearchEnSignupCta);
	const isSearchCtaEnabled = searchCtaExperiment.type === 'enrolled' && searchCtaExperiment.value;

	const { fireUIEvent } = useFireAnalyticsEvents();
	const intl = useIntl();

	// Use ref to track current fireUIEvent to avoid recreating SearchHelper
	const fireUIEventRef = useRef(fireUIEvent);
	fireUIEventRef.current = fireUIEvent;

	const isSearchIndexV3_2Enabled = checkFeatureFlagGate(featureFlags.wacSearchIndexV3_2);
	const searchIndex =
		process.env['NODE_ENV'] === 'production'
			? isSearchIndexV3_2Enabled
				? ALGOLIA_SEARCH_INDEX_NAME_V3_2
				: ALGOLIA_SEARCH_INDEX_NAME_V3
			: ALGOLIA_DEV_SEARCH_INDEX_V3;
	const facets = [ALGOLIA_FACET_KEY_CATEGORY, ALGOLIA_FACET_KEY_LOCALE];

	useFeatureExposed('wac-search-indexv3', 'Test');

	// Fire WAC AI Search V2 experiment exposure
	useEffect(() => {
		if (wacAISearchSummaryExperiment && wacAISearchSummaryExperiment.type === 'enrolled') {
			wacAISearchSummaryExperiment.fireExposure();
		}
	}, []);

	useEffect(() => {
		analyticsClient &&
			createAnalyticsEvent({
				eventName: 'searchText',
				eventType: 'track',
				action: 'loaded',
				actionSubject: 'searchPage',
				actionSubjectId: 'searchPageForm',
				source: 'searchPage',
				eventContainer: 'load',
				attributes: {
					searchText: searchText,
					product: 'search',
					index: { searchIndex },
				},
			}).fire();
	}, [analyticsClient, createAnalyticsEvent]);

	useEffect(() => {
		searchHelper.current = new SearchHelper(searchIndex, facets);

		// TODO: fetch filter category names from Algolia instead of hard-coding
		searchHelper.current?.receiveUpdate((results: algoliasearchHelper.SearchResults) => {
			const numberOfHits = results.nbHits;
			const localHits = results.hits.map((hit) => {
				return {
					...hit,
					queryID: results.queryID,
				} as BXPSearchResult;
			});
			const hasNextPage = results.nbPages - 1 > results.page;
			const queryId = results.queryID as string;

			const facetValuesResponse = results.getFacetValues(ALGOLIA_FACET_KEY_CATEGORY, {});

			// check if this is a filtered
			const refinements = results.getRefinements() || [];
			const isUsingFilter = !!refinements.find(
				(refinement) => refinement.attributeName === 'category',
			);

			// don't reset filter values if an existing search is just being filtered
			if (Array.isArray(facetValuesResponse) && !isUsingFilter) {
				let newFacetValuesState = {};
				facetValuesResponse.forEach((result) => {
					newFacetValuesState = {
						...newFacetValuesState,
						[result.name]: result.count,
					};
				});
				setFacetValues(newFacetValuesState);
			}

			if (hasNextPage) {
				setSearchState('hasMoreResult');
			} else if (localHits.length === 0) {
				setSearchState('noResult');
			} else {
				setSearchState('hasResult');
			}

			setTotalNumberOfHits(numberOfHits);
			setHits((prevResult) => [...prevResult, ...localHits]);
			setQueryId(queryId);

			// Use ref to get the current fireUIEvent function
			fireUIEventRef.current({
				eventName: 'searchResultsPopulated',
				eventType: 'ui',
				action: 'populated',
				actionSubject: 'searchResults',
				source: 'searchPage',
				eventContainer: 'searchResults',
				attributes: {
					resultsCount: numberOfHits,
					searchQuery: results.query || latestSearchText,
					queryId: results.queryID as string,
					currentFilter: currFilter,
					product: 'search',
					index: { searchIndex },
				},
			});
		});

		// Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		window.addEventListener('popstate', () => {
			setSearchResults();
		});
		setSearchResults();
		// Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const setSearchResults = () => {
		const search = window.location.search;
		const params = new URLSearchParams(search);
		const searchParam = params.get('q');
		const facet = params.get('f') || '';

		// if facet it present on original page load, set a facetValue so that it displays in filters
		if (facet) {
			setFacetValues({ [facet]: 1 });
		}
		if (searchParam) {
			const filter = facet ? (facet as CategoryFilterName) : 'All';
			resetState(searchParam, filter);
			triggerSearch(filter, searchParam);

			createAnalyticsEvent({
				eventName: 'browserButton',
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'browserButton',
				source: 'searchPage',
				eventContainer: 'browser',
				attributes: {
					searchText: searchParam,
					keywordSearchQuery: searchParam,
					product: 'search',
					index: { searchIndex },
				},
			}).fire();
		}
	};

	function resetState(searchParam: string, filter: CategoryFilterName) {
		setHits([]);
		setSearchState('loading');
		setSearchText(searchParam);
		setLatestSearchText(searchParam);
		setCurrFilter(filter);
	}

	function triggerSearch(filter: CategoryFilterName, searchParam: string) {
		searchHelper.current?.removeFacet(ALGOLIA_FACET_KEY_CATEGORY);
		searchHelper.current?.setQuery(searchParam);
		searchHelper.current?.setFacet(ALGOLIA_FACET_KEY_LOCALE, intl.locale || 'en');
		if (filter && filter !== 'All') {
			searchHelper.current?.setFacet(ALGOLIA_FACET_KEY_CATEGORY, filter);
		}
		if (isAISearchSummaryEnabled) {
			const searchWordCount = searchParam.split(' ').length;
			if (searchWordCount > TITLE_ONLY_SEARCH_WORD_COUNT_THRESHOLD) {
				searchHelper.current?.setQueryParameter('restrictSearchableAttributes', ['title']);
			} else {
				searchHelper.current?.removeQueryParameter('restrictSearchableAttributes');
			}
		}
		searchHelper.current?.search();
	}

	function onSearch(filter?: CategoryFilterName) {
		if (searchText) {
			if (!filter) filter = 'All';
			resetState(searchText, filter);
			triggerSearch(filter, searchText);
			setLatestSearchText(searchText);

			if (filter && filter !== 'All') {
				window.history.pushState({}, '', `?q=${searchText}&f=${filter}`);
			} else {
				window.history.pushState({}, '', `?q=${searchText}`);
			}

			createAnalyticsEvent({
				eventName: 'searchText',
				eventType: 'ui',
				action: 'submitted',
				actionSubject: 'searchInput',
				actionSubjectId: 'searchPageForm',
				source: 'searchPage',
				eventContainer: 'searchInput',
				attributes: {
					searchQuery: searchText,
					keywordSearchQuery: searchText,
					product: 'search',
					index: { searchIndex },
				},
			}).fire();
			if (searchInputRef.current) {
				searchInputRef.current.blur();
			}
		}
		setIsActive(true);
	}

	const renderClearButton = () => {
		if (searchText) {
			return (
				<button
					onClick={handleClear}
					css={clearButtonCss}
					aria-label="Clear search suggestions"
					data-testid="clear-search-button"
				>
					<CrossIcon label="Clear search input" size="small" color={token('color.text')} />
				</button>
			);
		}
		return null;
	};

	function gotoNextPage() {
		searchHelper.current?.goToNextPage();
	}

	function onSearchLinkClick(objectId: string, position: number) {
		const anonymousId = Cookie.readCookie('ajs_anonymous_id');
		let decodedAnonymousId;

		if (anonymousId) {
			// the cookie value is URI encoded with quotes on the outside, so decode and remove them
			decodedAnonymousId = decodeURIComponent(anonymousId).replace(/['"]+/g, '');
		}
		searchHelper.current?.sendClickEvent({
			objectId,
			position,
			queryId,
			userToken: decodedAnonymousId as string,
		});
	}

	const translated = {
		searchAtlassian: intl.formatMessage({
			id: 'wac.search.pages.search.atlassian',
			defaultMessage: 'Search Atlassian',
		}),
		submitSearch: intl.formatMessage({
			id: 'wac.search.pages.submit',
			defaultMessage: 'Submit',
		}),
		viewMore: intl.formatMessage({
			id: 'wac.search.pages.view.more',
			defaultMessage: 'View more',
		}),
		endOfResults: intl.formatMessage({
			id: 'wac.search.pages.end.of.result',
			defaultMessage: 'End of results',
		}),
	};

	const filterTranslationMap: FilterTranslations = {
		All: intl.formatMessage({
			id: 'wac.search.pages.filter.all',
			defaultMessage: 'All',
		}),
		Articles: intl.formatMessage({
			id: 'wac.search.pages.filter.developer.articles',
			defaultMessage: 'Articles',
		}),
		'Developer Resources': intl.formatMessage({
			id: 'wac.search.pages.filter.developer.resources',
			defaultMessage: 'Developer Resources',
		}),
		Events: intl.formatMessage({
			id: 'wac.search.pages.filter.events',
			defaultMessage: 'Events',
		}),
		Marketplace: intl.formatMessage({
			id: 'wac.search.pages.filter.marketplace',
			defaultMessage: 'Marketplace',
		}),
		'Purchasing FAQ': intl.formatMessage({
			id: 'wac.search.pages.filter.purchasing.faq',
			defaultMessage: 'Purchasing FAQ',
		}),
	};

	const searchStateWithResultAndIsNotLoading = (searchState: string | null) => {
		return searchState && searchState !== 'noResult' && searchState !== 'loading';
	};

	return (
		<div
			className="ais-InstantSearch"
			css={[containerCss, isAISearchSummaryEnabled && aiEnabledContainerCss]}
		>
			<div css={[searchContainerCss, isAISearchSummaryEnabled && aiEnabledSearchContainerCss]}>
				<div
					css={[
						searchInputContainerCss,
						isAISearchSummaryEnabled && aiEnabledSearchResultsContainerCss,
					]}
				>
					<div css={searchBarContainerCss}>
						<input
							type="text"
							value={searchText}
							onChange={handleChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							onKeyDown={(e: KeyboardEvent) => {
								if (e.key === 'Enter') onSearch();
							}}
							placeholder={intl.formatMessage({
								id: 'wac.search.pages.placeholder',
								defaultMessage: 'Search Keywords',
							})}
							css={searchInputCss}
							data-testid="search-input"
						/>
						{renderClearButton()}
						<button
							type="submit"
							onClick={() => onSearch()}
							onKeyDown={(e: KeyboardEvent) => {
								if (e.key === 'Enter') onSearch();
							}}
							css={[searchSubmitCss, isAISearchSummaryEnabled && aiEnabledSearchButtonCss]}
							aria-label={translated.submitSearch}
							data-testid="submit-search-button"
						>
							<SearchIcon
								size="medium"
								label="Submit search"
								color={isActive ? token('color.text.accent.blue') : token('color.text')}
							/>
						</button>
					</div>
				</div>
				<div
					css={[
						filtersContainerCss,
						!isAISearchSummaryEnabled &&
							!!searchStateWithResultAndIsNotLoading(searchState) &&
							filtersContainerWithResultCss,
					]}
				>
					{!!searchStateWithResultAndIsNotLoading(searchState) &&
						Object.entries(filterTranslationMap).map(
							([filterName, translatedFilterName]) =>
								(facetValues.hasOwnProperty(filterName) || filterName === 'All') && (
									<button
										key={filterName}
										css={[
											filterButtonsCss,
											currFilter === (filterName as CategoryFilterName) && selectedFilterCss,
										]}
										onClick={() => {
											onSearch(filterName as CategoryFilterName);
										}}
									>
										{translatedFilterName}
									</button>
								),
						)}
				</div>
				<div css={[resultsContainerCss, isAISearchSummaryEnabled && aiEnabledContainerCss]}>
					{isAISearchSummaryEnabled && latestSearchText !== '' && currFilter === 'All' && (
						<>
							<AISummary query={latestSearchText} />
							<hr
								role="presentation"
								css={css({
									marginBlockStart: token('space.300'),
									marginBlockEnd: token('space.500'),
									border: `1px solid ${wacToken('color.neutral.light200')}`,
								})}
							/>
						</>
					)}
					<div css={[isAISearchSummaryEnabled && aiEnabledSearchResultsContainerCss]}>
						{searchState &&
							searchState !== 'loading' &&
							renderSearchResultsMessage(numberOfHits, latestSearchText, currFilter)}
						{hits.map(({ objectID, title, description, url, queryID }, index) => (
							<Result
								key={objectID}
								index={index}
								title={title}
								description={description}
								href={url}
								objectId={objectID}
								queryId={queryID}
								onSearchLinkClick={onSearchLinkClick}
								isExperimentVariant={true}
								showTryButton={isSearchCtaEnabled}
								searchQuery={latestSearchText}
							/>
						))}
					</div>
					<div>
						{!!searchState &&
							searchState !== 'loading' &&
							(searchState === 'hasMoreResult' ? (
								<Button
									text={translated.viewMore}
									inputType="button"
									onClick={gotoNextPage}
									css={buttonCss}
									dataEventProps={{
										'data-event-component': 'linkButton',
										'data-event-container': 'supportSection',
										'data-product-key': 'search',
										'data-label': 'view-more',
										'data-event': 'clicked',
									}}
									aria-label={translated.viewMore}
								/>
							) : (
								<div css={endOfResultCss}>
									{searchState && searchState !== 'noResult' && (
										<span>{translated.endOfResults}</span>
									)}
								</div>
							))}
						{searchState && searchState === 'loading' && (
							<div css={spinnerContainerCss}>
								<div css={spinnerCss} />
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

/* Styles */
const containerCss = css({
	maxWidth: '960px',
	marginInline: 'auto',
	minHeight: '1000px',
	display: 'flex',
	[media.below.md]: {
		justifyContent: 'center',
	},
});

const aiEnabledContainerCss = css({
	maxWidth: '1192px',
});

const aiEnabledSearchContainerCss = css({
	width: '1192px',
});

const aiEnabledSearchResultsContainerCss = css({
	maxWidth: '700px',
});

const searchContainerCss = css({
	paddingBlock: '32px',
	justifyContent: 'center',
	width: '700px',
	[media.below.sm]: {
		paddingBlockStart: '8px',
		maxWidth: '390px',
	},
});

const searchBarContainerCss = css({
	'box-sizing': 'border-box',
	display: 'flex',
	position: 'relative',
	alignItems: 'center',
	width: '100%',
	paddingInlineEnd: '5px',
	'margin-bottom': '10px',
	'@media (max-width: 600px)': {
		marginBlock: '0 10px',
		maxWidth: '100%',
	},
});

const searchInputContainerCss = css({
	display: 'flex',
	alignItems: 'center',
	position: 'relative',
	width: '100%',
	marginInline: '20px',
	[media.below.md]: {
		width: 'auto',
	},
});

const searchInputCss = css({
	border: 'none',
	alignItems: 'center',
	backgroundColor: '#F4f5f7',
	width: '100%',
	borderRadius: '40px',
	display: 'flex',
	lineHeight: '1em',
	padding: '8px 24px',
	margin: '0',
	position: 'relative',
	paddingInlineEnd: '40px',
	height: '42px',
	justifyContent: 'space-between',
	'flex-shrink': '0',
	outline: 'none',
	fontSize: '16px',
	fontWeight: '400',
	color: wacToken('color.neutral.dark200'),
	fontFamily: 'inherit',
	caretColor: '#1868DB',
	'@media (max-width: 600px)': {
		fontSize: '14px',
		paddingBlock: '6px',
		paddingInline: '16px',
		blockSize: '32px',
		borderRadius: '20px',
	},

	':focus': {
		/* eslint-disable local/ensure-wac-token-usage */
		borderColor: 'rgba(62, 52, 211, 1)',
		outline: 'medium none currentColor',
	},
});

const searchSubmitCss = css({
	alignSelf: 'center',
	backgroundColor: 'transparent',
	border: 'none',
	height: '32px',
	width: '32px',
	insetInlineEnd: '10px',
	position: 'absolute',
	insetBlockStart: '50%',
	transform: 'translateY(-50%)',

	':hover': {
		cursor: 'pointer',
	},
});

const clearButtonCss = css({
	position: 'absolute',
	/* eslint-disable-next-line */
	right: '55px',
	/* eslint-disable-next-line */
	top: '50%',
	transform: 'translateY(-50%)',
	background: 'transparent',
	border: 'none',
	color: wacToken('color.neutral.light700'),
	cursor: 'pointer',
	fontSize: '18px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '14px',
	height: '14px',
	borderRadius: '50%',
});

const spinnerContainerCss = css({
	display: 'flex',
	justifyContent: 'center',
	marginBlock: '50px',
});

const rotation = keyframes({
	from: {
		transform: 'rotate(0deg)',
	},
	to: {
		transform: 'rotate(360deg)',
	},
});

const spinnerCss = css({
	width: '1em',
	height: '1em',
	border: `3px solid ${wacToken('color.primary.blue')}`,
	borderBlockEnd: 'transparent',
	borderRadius: '50%',
	display: 'inline-block',
	position: 'relative',
	animation: `${rotation} 1s linear infinite`,
	'::after': {
		content: '',
		position: 'absolute',
		border: '6px solid transparent',
		marginBlockStart: '7px',
		borderInlineEndColor: wacToken('color.primary.blue'),
		transform: 'rotate(-40deg)',
	},
});

const endOfResultCss = css({
	'margin-top': '25px',
	'text-align': 'center',
});

const filtersContainerCss = css({
	width: '100%',
	display: 'flex',
	gap: '16px',
	'flex-wrap': 'wrap',
	'border-bottom': 'none',
	'padding-bottom': '10px',
	marginInline: '20px',
	'margin-top': '5px',
	[media.below.sm]: {
		width: 'auto',
	},
});

const filtersContainerWithResultCss = css({
	'border-bottom': `1px solid ${wacToken('color.neutral.light200')}`,
});

const filterButtonsCss = css({
	display: 'flex',
	'align-items': 'center',
	height: '36px',
	padding: '8px 16px',
	'border-radius': '100px',
	'border-style': 'none',
	backgroundColor: wacToken('color.neutral.light200'),
	cursor: 'pointer',
});

const selectedFilterCss = css({
	backgroundColor: '#e9f2fe',
});

const buttonCss = css({
	margin: '0 auto',
	display: 'block',
	borderRadius: '40px',
	backgroundColor: wacToken('color.extended.blue700'),
	padding: '8px 24px',
});

const resultsContainerCss = css({
	marginInline: '20px',
	paddingBlockStart: '14px',
	width: '100%',
	[media.below.sm]: {
		width: 'auto',
	},
});

const aiEnabledSearchButtonCss = css({
	lineHeight: '10px',
	marginInlineEnd: '8px',
});
